import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { useLayoutMargins } from '@pbk-web/state';
import type { FCWithChildren } from '@pbk-web/types';
import { dataAttributeProps, onlyText } from '@pbk-web/utils';
import type { CSSProperties } from 'react';
import { createContext, Suspense, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';

import { useModal } from '../../context/ModalContext';
import { XIcon } from '../icons';
import PageLoader from '../PageLoader';
import { Heading, HeadingSize } from '../text/Heading';
import Tooltip from '../tooltip/Tooltip';

const Icon: FCWithChildren = (props) => {
  return (
    <div className="flex justify-center px-3 py-4" data-testid="modal-icon">
      {props.children}
    </div>
  );
};

type HeaderProps = {
  defaultStyle?: boolean;
  align?: 'left' | 'center';
  underline?: boolean;
};

const Header: FCWithChildren<HeaderProps> = (props) => {
  const { defaultStyle = true, children, align, underline } = props;
  const { modalId } = useInternalModal();
  return (
    <Tooltip text={onlyText(children)} truncatedTextMode>
      {(tooltip) => {
        return (
          <div
            {...tooltip}
            // Tooltip above contains a tabIndex={0}, we override it here
            tabIndex={-1}
            className={`pr-4 ${underline ? 'border-greyscale-5 border-b' : ''}`}
          >
            {defaultStyle ? (
              <Heading
                id={`modal-title-${modalId}`}
                data-testid="modal-header"
                size={HeadingSize.CUSTOM}
                className={`${align !== 'left' ? 'text-center' : ''} text-pbk-32 pl block px-6 py-4 pb-2 font-normal empty:hidden`}
              >
                {children}
              </Heading>
            ) : (
              children
            )}
          </div>
        );
      }}
    </Tooltip>
  );
};

const SubHeader: FCWithChildren = (props) => {
  return <div className="text-pbk-20 px-6 pt-0 pb-4 text-center">{props.children}</div>;
};

type BodyProps = {
  padding?: string;
  height?: string;
  maxHeight?: string;
};

const Body: FCWithChildren<BodyProps> = (props) => {
  const { children, padding = 'px-6 py-4', height = 'flex-1 min-h-0', maxHeight } = props;
  const { modalId, bodyMaxHeightCss, overflow } = useInternalModal();

  return (
    <div
      id={`modal-description-${modalId}`}
      data-testid="modal-body"
      className={`${padding} text-text-2 ${height} min-h-15 ${maxHeight ?? bodyMaxHeightCss} ${overflow ? 'overflow-y-auto' : ''}`}
    >
      <Suspense
        fallback={
          <div className="p-8">
            <PageLoader loading isSuspense loaderSize={16} />
          </div>
        }
      >
        {children}
      </Suspense>
    </div>
  );
};
type FooterProps = {
  padding?: string;
  justification?: string;
  topline?: boolean;
};

const Footer: FCWithChildren<FooterProps> = (props) => {
  const { children, padding = 'px-6 py-4', justification = 'justify-center', topline = true } = props;
  return (
    <div data-testid="modal-footer" className={`${padding} flex items-center ${justification} gap-4 ${topline ? 'border-greyscale-5 border-t' : ''}`}>
      {children}
    </div>
  );
};

type ModalType = FCWithChildren<{
  uncloseable?: boolean;
  // Picking the CSSProperties, because the full type errors for some reason
  extraStyles?: Pick<CSSProperties, 'margin' | 'marginLeft' | 'marginTop' | 'maxWidth' | 'minHeight'>;
}> & {
  Icon: typeof Icon;
  Header: typeof Header;
  SubHeader: typeof SubHeader;
  Body: typeof Body;
  Footer: typeof Footer;
};

const InternalModalContext = createContext({ priority: 0, modalId: '', bodyMaxHeightCss: 'max-h-[calc(100vh-16rem)]', overflow: true });
const useInternalModal = () => useContext(InternalModalContext);

export const Modal: ModalType = (props) => {
  const { children, uncloseable, extraStyles } = props;
  const {
    open,
    modalWidth = 'w-3/5',
    modalMaxWidth = 'max-w-[calc(100vw-4rem)]',
    showCloseButton = true,
    onClose,
    closeOnBackdropClick,
    className,
    priorityOverride,
    position = 'center',
    maxHeight = 'calc(100vh - 4rem)',
    height,
    overflow,
  } = useModal();
  const { t } = useTranslation('common');
  const leftLayoutMargin = useLayoutMargins((x) => x.value);

  const modalId = useMemo(() => uuid(), []);
  const [isExiting, setIsExiting] = useState(false);

  const { refs, context } = useFloating({
    open: open,
    onOpenChange: (o) => {
      !o && onClose && onClose();
    },
  });

  const click = useClick(context);
  const dismiss = useDismiss(context, {
    escapeKey: true,
    enabled: closeOnBackdropClick,
    outsidePressEvent: 'mousedown',
    outsidePress: (event) => {
      return (event.target as HTMLElement)?.dataset['backdrop'] === modalId;
    },
  });

  const role = useRole(context);

  const { getFloatingProps } = useInteractions([click, dismiss, role]);

  const animationClass = useMemo(() => {
    return isExiting
      ? position === 'end'
        ? 'animate-slide-out-to-right'
        : 'animate-fade-out-scale'
      : position === 'end'
        ? 'animate-slide-in-from-right'
        : 'animate-fade-in-scale';
  }, [isExiting, position]);

  const containerClass = useMemo(() => {
    return `${className} relative min-w-96 flex flex-col overflow-hidden rounded-[5px] bg-white shadow-xl ${leftLayoutMargin} ${modalWidth} ${modalMaxWidth} ${animationClass}`;
  }, [className, leftLayoutMargin, modalWidth, modalMaxWidth, animationClass]);

  const handleClose = useCallback(() => {
    setIsExiting(true);
  }, []);

  const handleAnimationEnd = useCallback(() => {
    if (isExiting) {
      onClose && onClose();
      setIsExiting(false);
    }
  }, [isExiting, onClose]);

  return (
    open && (
      <FloatingPortal id="portal-root">
        <FloatingOverlay
          lockScroll
          style={{
            overflow: 'hidden',
            background: 'rgba(var(--pbk-color-greyscale-75), 0.5)',
            backdropFilter: 'blur(2px)',
            display: 'grid',
            placeItems: position,
            zIndex: 500 + (priorityOverride ?? 0) + 1,
          }}
          data-backdrop={modalId}
        >
          <FloatingFocusManager context={context} initialFocus={refs.floating}>
            {}
            <div
              ref={refs.setFloating}
              {...getFloatingProps({ style: { maxHeight, height, zIndex: 500 + (priorityOverride ?? 0) + 2, ...extraStyles } })}
              className={containerClass}
              data-testid="modal-container"
              aria-labelledby={`modal-title-${modalId}`}
              aria-describedby={`modal-description-${modalId}`}
              {...dataAttributeProps(props)}
              onClick={(e) => e.stopPropagation()}
              onAnimationEnd={handleAnimationEnd}
            >
              {onClose && showCloseButton && !uncloseable && (
                <XIcon
                  aria-label={t('aria-label.close')}
                  data-testid="modal-close"
                  // `z-10`: the close button is painted before the body, so any positioned
                  // content inside it (an input, a card, a sticky header) would otherwise sit
                  // on top of the one control that closes the modal.
                  className="absolute top-0 right-0 z-10 mt-[20px] mr-[20px] h-5 w-5"
                  onClick={handleClose}
                />
              )}
              <InternalModalContext.Provider
                value={{
                  priority: (priorityOverride ?? 0) + 2,
                  modalId,
                  bodyMaxHeightCss: position !== 'center' ? 'max-h-[calc(100vh-11.2rem)]' : 'max-h-[calc(100vh-16rem)]',
                  overflow: overflow ?? true,
                }}
              >
                <Suspense fallback={null}>{children}</Suspense>
              </InternalModalContext.Provider>
            </div>
          </FloatingFocusManager>
        </FloatingOverlay>
      </FloatingPortal>
    )
  );
};

Modal.Icon = Icon;
Modal.Header = Header;
Modal.Body = Body;
Modal.Footer = Footer;
Modal.SubHeader = SubHeader;
